import { useState, useMemo, useEffect } from 'react';
import { Box, Tab } from '@mui/material';
import { NoteComposer } from '@components/notesDialog/components/NoteComposer';
import { NotesList } from '@components/notesDialog/components/NotesList';
import { NotesEntitiesSideMenu } from '@components/notesDialog/components/NotesEntitiesSideMenu';
import { NotesDialogContext, NotesEntityItem } from '@components/notesDialog/types';
import { useNotesScope } from '@components/notesDialog/useNotesScope';
import { useSearchNotes } from '@services/note';

import { Tabs } from '@components/tabs/Tabs';
import { useSearchPolicyTransactions } from '@services/policy/policies';
import { PolicyTransactionTypes } from '@models/policyTransaction';

import { SearchField } from '@components/search';
import { Button } from '@components/index';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { emptyMessageListItems } from '@pages/QuoteDetails/sidebarItems/notes/Notes';
import { EmptyMessagesList } from '@pages/QuoteDetails/sidebarItems/messages/EmptyMessagesList';
import { PinnedNotesBar } from '@components/notesDialog/components/PinnedNotesBar';
import Loading from '@components/Loading';
import { usePolicySearchForCustomer } from '@services/policy/usePolicySearchForCustomer';
import { getFormattedPolicyNumber } from '@utils/policy';
import { useQuoteSearchForCustomer } from '@services/quotes/useQuoteSearch';
import { GET_ALL_PAGE_LIMIT } from '@services/config';
import { InternalQuoteStatus, QuoteStatus, QuoteType, QuoteTypeLabels } from '@models/quotes';
import { NoteSearchRequestQuery } from '@lio/api-types';

export interface NotesDialogContentProps {
  context: NotesDialogContext;
  customerId: string; // required for customer context
  policyId?: string; // required for policy context
  quoteId?: string;
}

export const NotesDialogContent = ({ context, customerId, policyId, quoteId }: NotesDialogContentProps) => {
  const { isMobileScreen } = useGetScreenSize();

  const [activeTab, setActiveTab] = useState<string>(
    context === 'policy' ? 'all' : context === 'customer' ? 'customer' : 'all',
  );
  const [selectedEntity, setSelectedEntity] = useState<NotesEntityItem | null>(null);

  const [searchText, setSearchText] = useState('');
  const { data: policyData } = usePolicySearchForCustomer(
    {
      customerId,
      pageLimit: GET_ALL_PAGE_LIMIT,
    },
    context === 'customer',
  );

  const { policyTransactions } = useSearchPolicyTransactions({
    policyId,
    pageLimit: GET_ALL_PAGE_LIMIT,
    includeCanceled: false,
  });

  const { data: quotesData } = useQuoteSearchForCustomer(
    {
      customerId: customerId,
      pageLimit: GET_ALL_PAGE_LIMIT,
      statuses: [
        // apparently these too
        QuoteStatus.Expired.queryParam,
        QuoteStatus.Canceled.queryParam,
        QuoteStatus.Rejected.queryParam,
        QuoteStatus.Declined.queryParam,
        // active
        QuoteStatus.ReadyForRating.queryParam,
        QuoteStatus.Rated.queryParam,
        QuoteStatus.Submission.queryParam,
        QuoteStatus.Underwritten.queryParam,
      ],
      sort: '-createdAt',
    },
    context === 'customer',
  );

  const policies: NotesEntityItem[] = useMemo(
    () =>
      policyData?.items?.map((p) => ({
        id: p.id,
        entity: 'policy' as const,
        title: `Policy #${getFormattedPolicyNumber(p.policyNumberPrefix, p.policyNumber.policyNumber)}`,
        effectiveOn: p.effectiveOn,
        expiresOn: p.expiresOn,
        metaLabels: [`${p.boundQuote?.offering?.insuranceProduct?.name}`, `${p.packageLabel}`],
      })) || [],
    [policyData],
  );

  const quotes: NotesEntityItem[] = useMemo(
    () =>
      quotesData?.items?.map((q) => ({
        id: q.id,
        entity: 'quote' as const,
        title: `Quote ${getFormattedPolicyNumber('#', q.policyNumber?.policyNumber || '')}`,
        effectiveOn: q.effectiveOn,
        metaLabels: [`${q.offering?.insuranceProduct?.name}`, `${InternalQuoteStatus[q.statusId]}`],
      })) || [],
    [quotesData],
  );

  // Transactions only for the selected policy (if any)

  const transactions: NotesEntityItem[] = useMemo(
    () =>
      policyTransactions?.items
        ?.filter((t) =>
          [
            PolicyTransactionTypes.NewBusiness,
            PolicyTransactionTypes.Endorsement,
            PolicyTransactionTypes.Renewal,
            PolicyTransactionTypes.Rewrite,
          ].includes(t.transactionType),
        )
        ?.sort((a, b) => {
          const dateA = a.effectiveOn ? new Date(a.effectiveOn).getTime() : 0;
          const dateB = b.effectiveOn ? new Date(b.effectiveOn).getTime() : 0;
          const processedOnA = a.processedOn ? new Date(a.processedOn).getTime() : 0;
          const processedOnB = b.processedOn ? new Date(b.processedOn).getTime() : 0;
          if (dateA === dateB) return processedOnB - processedOnA;
          return dateB - dateA;
        })
        ?.map((item) => ({
          id: item?.quote?.id ?? '',
          entity: 'transaction' as const,
          title: QuoteTypeLabels[item.transactionType as QuoteType],
          effectiveOn: item.effectiveOn ? new Date(item.effectiveOn).toISOString() : '',
        })) ?? [],
    [policyTransactions?.items],
  );

  const scope = useNotesScope({
    context,
    selectedTab: activeTab,
    customerId,
    quoteId,
    basePolicyId: policyId,
    selectedEntity,
  });

  const { searchNotes, isFetchingNextPage, fetchNextPage, isRefetching } = useSearchNotes({
    ...scope.params,
    sort: '-createdAtOrReplacedAt',
    searchText: searchText.length > 2 ? searchText : undefined,
  } as NoteSearchRequestQuery);
  const [showAddNote, setShowAddNote] = useState(true);

  const isSearching = isRefetching;

  // Separate pinned notes query (limited set) for header bar & dialog
  const { searchNotes: pinnedNotesQuery } = useSearchNotes({
    ...scope.params,
    sort: '-pinnedAt,-createdAtOrReplacedAt',
    isPinned: true,
    pageLimit: 100,
  } as NoteSearchRequestQuery);

  const pinnedNotes = useMemo(
    () => pinnedNotesQuery?.pages?.flatMap((p) => p.items).filter((n: any) => n.isPinned && !n.deletedAt) ?? [],
    [pinnedNotesQuery],
  );

  const unpinned = useMemo(
    () => searchNotes?.pages?.flatMap((p) => p.items)?.filter((x) => !x.deletedAt) || [],
    [searchNotes],
  );

  // When user navigates to the underlying transactions tab, auto-select the first transaction if none selected yet
  useEffect(() => {
    if (context === 'policy' && activeTab === 'transactions' && !selectedEntity && transactions.length > 0) {
      setSelectedEntity(transactions[0]);
    }
  }, [context, activeTab, selectedEntity, transactions]);

  const isPolicyContext = context === 'policy';
  const isCustomerContext = context === 'customer';
  const isQuoteDetailContext = context === 'quote-detail';

  const isSearchEnabled = searchText.length > 2;

  useEffect(() => {
    if (!isMobileScreen) return;
    if (isPolicyContext) {
      setActiveTab('all');
      setSelectedEntity(null);
    } else if (isCustomerContext) {
      setActiveTab('customer');
      setSelectedEntity(null);
    }
  }, [isMobileScreen, isPolicyContext, isCustomerContext]);

  const totalNotes = searchNotes?.pages?.[0]?.pagination?.total || 0;
  const loadedItems = unpinned?.length;
  const hasMoreToLoad = totalNotes > (loadedItems || 0);

  const tabs = (
    isCustomerContext
      ? [
          { value: 'customer', label: 'Customer Notes', hidden: false },
          { value: 'policy', label: 'Policy Notes', hidden: isMobileScreen || policies.length === 0 },
          { value: 'quote', label: 'Quote Notes', hidden: isMobileScreen || quotes.length === 0 },
        ]
      : isPolicyContext
        ? [
            { value: 'all', label: 'All Notes', hidden: false },
            { value: 'policy', label: 'Policy Notes', hidden: isMobileScreen },
            { value: 'transactions', label: 'Transaction Notes', hidden: isMobileScreen },
          ]
        : []
  ).filter((t) => !t.hidden);

  return (
    <Box sx={sxProps.layout.root}>
      <Box
        id='notes-sub-header'
        sx={[
          sxProps.layout.subHeader,
          isQuoteDetailContext
            ? {
                justifyContent: 'flex-end',
              }
            : {},
        ]}
      >
        {/* Unified Tabs Rendering (customer & policy contexts) */}
        {tabs && tabs.length > 0 && (
          <Tabs
            type='miniShort'
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              // Generic entity priming logic
              if (v === 'policy' && isCustomerContext) {
                setSelectedEntity(policies[0] ?? null);
              } else if (v === 'transactions' && isPolicyContext) {
                setSelectedEntity(transactions[0] ?? null);
              } else if (v === 'quote') {
                setSelectedEntity(quotes[0] ?? null);
              } else {
                setSelectedEntity(null);
              }
            }}
          >
            {tabs.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
        )}

        <Box sx={sxProps.layout.subHeaderActions}>
          {scope.composerEnabled && !isSearchEnabled && (
            <Button
              sx={sxProps.buttons.toggleComposer}
              variant='text'
              size='small'
              onClick={() => setShowAddNote(!showAddNote)}
            >
              {showAddNote
                ? isMobileScreen
                  ? 'Hide Add Note'
                  : 'Hide Add New Note'
                : isMobileScreen
                  ? 'Show Add Note'
                  : 'Show Add New Note'}
            </Button>
          )}
          <SearchField
            size='small'
            overrideValue={searchText}
            onChange={(value) => setSearchText(value)}
            placeholder='Search notes'
          />
        </Box>
      </Box>
      <Box id='notes-dialog-body' sx={sxProps.layout.bodyWrapper}>
        {/* Side menu logic */}
        {isCustomerContext && activeTab === 'policy' && (
          <NotesEntitiesSideMenu
            items={policies.map((p) => ({
              id: p.id,
              entity: 'policy' as const,
              title: p.title,
              effectiveOn: p.effectiveOn,
              expiresOn: p.expiresOn,
              metaLabels: p.metaLabels,
            }))}
            selectedId={selectedEntity?.entity === 'policy' ? selectedEntity.id : null}
            onSelect={(item) => {
              setSelectedEntity({ ...item, entity: 'policy' });
            }}
            emptyLabel='No policies'
            id='policies-side-menu'
          />
        )}
        {isCustomerContext && activeTab === 'quote' && (
          <NotesEntitiesSideMenu
            items={quotes.map((q) => ({
              id: q.id,
              entity: 'quote' as const,
              title: q.title,
              effectiveOn: q.effectiveOn,
              expiresOn: q.expiresOn,
              metaLabels: q.metaLabels,
            }))}
            selectedId={selectedEntity?.entity === 'quote' ? selectedEntity.id : null}
            onSelect={(item) => {
              setSelectedEntity({ ...item, entity: 'quote' });
            }}
            emptyLabel='No quotes'
            id='quotes-side-menu'
          />
        )}
        {isPolicyContext && activeTab === 'transactions' && (
          <NotesEntitiesSideMenu
            items={transactions.map((t) => ({
              id: t.id,
              entity: 'transaction' as const,
              title: t.title,
              effectiveOn: t.effectiveOn,
            }))}
            selectedId={selectedEntity?.entity === 'transaction' ? selectedEntity.id : null}
            onSelect={(item) => {
              setSelectedEntity({ ...item, entity: 'transaction' });
            }}
            emptyLabel='No transactions'
            id='transactions-side-menu'
          />
        )}
        <Box sx={sxProps.layout.mainColumn}>
          {/* Composer */}
          <Box
            sx={[
              sxProps.composer.wrapper,
              scope.composerEnabled && showAddNote && !isSearchEnabled
                ? sxProps.composer.wrapperActive
                : sxProps.composer.wrapperHidden,
            ]}
          >
            {scope.composerEnabled && (
              <Box sx={showAddNote && !isSearchEnabled ? sxProps.composer.bodyVisible : sxProps.composer.bodyHidden}>
                <NoteComposer scope={scope} />
              </Box>
            )}
          </Box>

          <Box id='notes-container' sx={sxProps.notes.container}>
            <Box sx={sxProps.notes.innerColumn}>
              {!isSearchEnabled && pinnedNotes.length > 0 && <PinnedNotesBar pinnedNotes={pinnedNotes} />}

              {scope.composerEnabled && unpinned.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    paddingBottom: '8vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ width: 'fit-content' }}>
                    <EmptyMessagesList
                      title='Create a Note'
                      subtitle='If you want to save notes for yourself or the LIO team, enter them here.'
                      items={emptyMessageListItems}
                    />
                  </Box>
                </Box>
              ) : isSearching ? (
                <Box sx={{ marginTop: '30px', height: '100%', display: 'flex', justifyContent: 'center' }}>
                  <Loading />
                </Box>
              ) : (
                <NotesList
                  showMetadata={
                    (isCustomerContext && selectedEntity?.entity === 'policy') ||
                    (isPolicyContext && activeTab === 'all')
                  }
                  notes={unpinned}
                  searchBarText={isSearchEnabled ? searchText : undefined}
                  fetchMore={fetchNextPage}
                  isFetchingMore={isFetchingNextPage}
                  hasMore={hasMoreToLoad}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Consolidated style object to keep JSX clean
const sxProps = {
  layout: {
    root: {
      overflow: 'hidden',
      pt: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      flex: 1,
      minHeight: 0,
    },
    subHeader: {
      pl: 1,
      pr: 0.5,
      py: 0.5,
      display: 'flex',
      justifyContent: 'space-between',
      alignContent: 'center',
      alignItems: 'center',
      borderBottom: '1px solid',
      borderColor: 'border.default',
      '.MuiTabs-root': {
        padding: '0px !important',
        margin: '0 !important',
        height: '20px',
      },
    },
    subHeaderActions: {
      display: 'flex',
    },
    bodyWrapper: {
      overflow: 'hidden',
      display: 'flex',
      flex: 1,
    },
    mainColumn: {
      minHeight: { xs: '100vh', sm: '80vh' },
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      overflow: 'hidden',
    },
  },
  buttons: {
    toggleComposer: { padding: 0, mr: 1 },
  },
  composer: {
    wrapper: { pl: 1.5, pr: 2.5 },
    wrapperHidden: { height: 0 },
    wrapperActive: { borderBottom: '1px solid gray', borderColor: 'border.main' },
    bodyVisible: {},
    bodyHidden: { visibility: 'hidden', height: 0, p: 0 },
  },
  notes: {
    container: {
      backgroundColor: 'background.main',
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      width: '100%',
      display: 'flex',
    },
    innerColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      width: '100%',
      minHeight: 0,
      flex: 1,
    },
  },
} as const;
